/** Small display helpers shared by both views. */

/**
 * `2026-08-01T10:00:00.000Z` -> `1 Aug`, or `1 Aug 2025` once it is not this
 * year. Dropping the year for recent dates keeps the pipeline table narrow,
 * which is where most of these are read.
 */
export function shortDate(iso: string | null): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";

  const sameYear = date.getUTCFullYear() === new Date().getUTCFullYear();
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
    timeZone: "UTC",
  });
}

/** Full timestamp for a tooltip, so the abbreviated date is never the only copy. */
export function fullDate(iso: string | null): string {
  if (!iso) return "";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : `${date.toUTCString()}`;
}

/**
 * Conversion as a percentage. `null` means "nobody has visited yet", which is
 * rendered as an em dash rather than 0% — a link nobody has opened has no
 * conversion rate, and showing 0% makes a brand-new partner look like a
 * failing one.
 */
export function percent(value: number | null): string {
  if (value === null) return "—";
  return `${(value * 100).toFixed(value >= 0.1 ? 0 : 1)}%`;
}

/** "3 days ago" style relative age, for the "last lead" column. */
export function relativeAge(iso: string | null): string {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "never";

  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

/** Turns the stored comma-separated `geo` column back into a readable list. */
export function geoList(geo: string | null): string {
  return geo?.split(",").filter(Boolean).join(", ") || "—";
}
